// The portrait (on home and both about pages): the X-ray, the face and lens dots, the lens
// sheen, and the butterfly, all driven by the engine's wing beats and the waves they send.
// Clicking the butterfly makes it flap. The page reacts to waves through `PortraitHooks`.
//
// The parts, in the order `run()` connects them:
//
//   data ──▶ outline (face-outline.ts; stays with the static print)
//     │
//     └────▶ layers (portrait-layers.ts) ──▶ engine ──▶ waves (portrait-waves.ts)
//                                              │
//                                              └──▶ monarch (home-monarch.ts)
//
// The live portrait's listeners and observers share one `AbortController`. A lost context
// ends them all, and the static print takes over. The element's connection signal
// (elements/jrsm-portrait.ts) ends them too, and releases the GPU.

import {
  ClickGesture,
  Engine,
  buildRenderStateInto,
  createRenderStateScratch,
  randomClickStrength,
  type DotGroups,
  type HalftoneData,
} from "@bfly/engine";
import { assertGroupsFit, loadGroups, loadHalftone } from "@bfly/engine/data";
import halftoneUrl from "../assets/data/halftone.bin.gz?url";
import halftoneHeaderUrl from "../assets/data/halftone.json?url";
import groupsUrl from "../assets/data/groups.json?url";
import { trackOutline } from "./face-outline.js";
import { HomeMonarch } from "../monarch/home-monarch.js";
import { motionReduced, onMotionChange } from "../page/motion.js";
import { onThemeChange } from "../page/theme.js";
import { PortraitLayers } from "./portrait-layers.js";
import { PortraitWaves } from "./portrait-waves.js";
import type { XraySource } from "./xray.js";
import type { PortraitHooks } from "../waves/waves.js";
import { WingPlaneMotion } from "../monarch/wing-pose.js";

// A theme change flaps the wings: a level 3 wave, firm and quick.
const THEME_FLAP_LEVEL = 3;
const THEME_FLAP_STRENGTH = 0.85;
const THEME_FLAP_SPEED = 1.25;

/** The portrait's data: the dots and their groups. */
export type PortraitData = readonly [HalftoneData, DotGroups];

/** Starts downloading the portrait data. The portrait element calls this as it upgrades, so
    the request doesn't wait for the rest of the page's setup. */
export function loadPortraitData(): Promise<PortraitData> {
  return Promise.all([
    loadHalftone({ header: halftoneHeaderUrl, binary: halftoneUrl }, { gzip: true }),
    loadGroups(groupsUrl),
  ]);
}

async function run(
  portrait: HTMLElement,
  hooks: PortraitHooks,
  loading: Promise<PortraitData>,
  connection: AbortSignal,
  xray: XraySource,
): Promise<void> {
  // The data loads before any WebGL, so the page gets the face's outline even when only the
  // static print shows.
  const [data, groups] = await loading;
  // If the portrait left the page while its data loaded, there's nothing to set up.
  if (connection.aborted) return;
  assertGroupsFit(groups, data.n);
  if (hooks.onOutline) trackOutline(portrait, data, hooks.onOutline, connection);

  const button = portrait.querySelector(".bfly-hit");
  if (!(button instanceof HTMLButtonElement)) throw new Error("Portrait: missing .bfly-hit");
  const hit = button;
  const layers = await PortraitLayers.create(portrait, data, groups);
  if (connection.aborted) {
    layers.dispose();
    return;
  }

  const live = new AbortController();
  const { signal } = live;
  // Frame state, reused so the render loop doesn't allocate here.
  const faceState = createRenderStateScratch();
  const wingMotion = new WingPlaneMotion();
  let onScreen = true;

  const engine = new Engine({ reducedMotion: motionReduced(), onFrame: draw });
  engine.world.ripples.held = () => hooks.wavesHeld?.() ?? false;
  const waves = new PortraitWaves(portrait, engine.world, hooks);
  const monarch = new HomeMonarch(portrait, layers.butterflyCanvas, hit, layers.butterflyData, {
    onModeChange: (mode) => layers.keepButterflyBuffer(mode !== "webgpu"),
    canAnimate: () => !motionReduced(),
    onFlap: () => engine.visualFlap(engine.world.time),
    onChange: draw,
  });
  monarch.setFarShade(layers.look.farWing.shade);

  // When the element disconnects, every listener and observer stops, the loop and the
  // butterfly stop, the page's waves end, and the GPU memory is released.
  connection.addEventListener(
    "abort",
    () => {
      live.abort();
      engine.stop();
      monarch.stop();
      waves.end();
      layers.dispose();
      hit.hidden = true;
      portrait.removeAttribute("data-live");
      portrait.removeAttribute("data-waves");
    },
    { once: true },
  );

  function draw(): void {
    if (signal.aborted) return;
    const world = engine.world;
    const wave = world.ripples.items.at(-1) ?? null;
    // The face and the lens scatter in each wave's ring from the butterfly (render-state.ts),
    // whether or not the field is running.
    buildRenderStateInto(faceState, world, wave, layers.layout.fScale);
    // The X-ray follows last frame's waves: they update after the layers draw.
    layers.setXrayStrength(world.reducedMotion ? 0 : waves.xrayLevel);
    layers.render(faceState);
    const wingStats = engine.wingStats();
    const wingsMove = !world.reducedMotion;
    const pose = wingMotion.update(world.time, wingStats, wingsMove);
    monarch.render(world.time, wingStats, wingsMove);
    if (monarch.drawsFallback) layers.renderButterfly(world.time, pose, wingStats.kind);
    waves.update();
    // Off screen or in a hidden tab, stop once no wave is live, so a ring never gets stuck
    // frozen on the background.
    if ((!onScreen || document.hidden) && !wave) engine.stop();
  }

  function syncLayout(): void {
    monarch.completeHandoff();
    layers.size();
    monarch.resize();
    waves.measure(layers.faceReach * layers.layout.fScale);
    draw();
  }
  let resizeFrame: number | null = null;
  window.addEventListener(
    "resize",
    () => {
      // Measure at most once a frame, so a burst of resizes ends with the final viewport.
      if (resizeFrame !== null) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        if (!signal.aborted) syncLayout();
      });
    },
    { signal },
  );
  window.addEventListener("scroll", () => waves.scrolled(), { passive: true, signal });
  syncLayout();

  // A lost context can't draw again on this visit, so the static print takes over.
  layers.onContextLost(() => {
    live.abort();
    engine.stop();
    monarch.stop();
    hit.hidden = true;
    portrait.toggleAttribute("data-static", true);
    // The loop has stopped, so a live wave would never end and its words would stay
    // restyled. End it here.
    waves.end();
  }, signal);

  // The first frame is up. (`data-waves`, set every frame, counts the live waves.)
  portrait.toggleAttribute("data-live", true);

  // A theme change reblends the dots with the new page color, in the theme's mode. If the
  // portrait is on screen and motion is on, it also flaps the wings.
  onThemeChange(
    () => {
      layers.applyLook();
      monarch.setFarShade(layers.look.farWing.shade);
      draw();
      if (onScreen && !motionReduced())
        engine.flap(
          engine.world.time,
          THEME_FLAP_LEVEL,
          THEME_FLAP_STRENGTH,
          THEME_FLAP_SPEED,
          "theme",
        );
    },
    { signal },
  );

  // A click flaps the wings at the click gesture's level: a lone click gets a random 1 to 3,
  // and a fast run of clicks gets 4. The wing accepts one click a second. A click while the
  // most waves are already live just flutters, with no new wave. With reduced motion the
  // button is hidden and clicks do nothing.
  const gesture = new ClickGesture();
  hit.addEventListener(
    "click",
    (event) => {
      if (motionReduced()) return;
      const level = gesture.click(event.timeStamp / 1000);
      engine.flap(engine.world.time, level, randomClickStrength(Math.random));
    },
    { signal },
  );

  // Off screen, the portrait stops its loop once its waves end (see `draw()`). Three WebGL
  // canvases and a WebGPU butterfly have no business drawing where nobody can see them.
  const visibility = new IntersectionObserver(([entry]) => {
    onScreen = entry?.isIntersecting ?? false;
    if (onScreen && !motionReduced() && !document.hidden) engine.start();
  });
  visibility.observe(portrait);
  signal.addEventListener("abort", () => visibility.disconnect());

  // Reduced motion keeps one still frame: no loop, no waves, and no butterfly button. The
  // system setting and the motion switch can both change mid-visit. The WebGPU butterfly
  // only starts once motion is on, so with reduced motion the WebGL butterfly draws the
  // still frame.
  let monarchStarted = false;
  function applyMotion(): void {
    const reduced = motionReduced();
    engine.world.reducedMotion = reduced;
    hit.hidden = reduced;
    if (reduced) {
      engine.calm();
      engine.stop();
      monarch.completeHandoff();
      // This frame also ends the waves on the page: with no live wave, it sends an empty
      // ring list and clears the X-ray.
      draw();
      return;
    }
    if (onScreen && !document.hidden) engine.start();
    if (!monarchStarted) {
      monarchStarted = true;
      void monarch.start();
    }
  }
  onMotionChange(applyMotion, { signal });
  applyMotion();

  // The X-ray downloads once the page lets it (after the first view), and only with motion
  // on: with reduced motion there are no waves to show it. A failed download only costs the
  // X-ray.
  let xrayAllowed = false;
  let xrayRequested = false;
  function requestXray(): void {
    if (!xrayAllowed || xrayRequested || motionReduced() || signal.aborted) return;
    xrayRequested = true;
    void xray
      .load()
      .then((dots) => layers.attachXray(dots))
      .catch((error: unknown) => {
        if (!signal.aborted) console.warn("Portrait: no X-ray", error);
      });
  }
  void xray.allowed.then(() => {
    xrayAllowed = true;
    requestXray();
  });
  onMotionChange(requestXray, { signal });
  // A hidden tab stops the loop once no wave is live (see `draw()`), and showing the tab
  // starts it again. The engine's clock adds up frame steps, so the motion picks up where it
  // left off.
  document.addEventListener(
    "visibilitychange",
    () => {
      if (!document.hidden && onScreen && !motionReduced()) engine.start();
    },
    { signal },
  );
}

/** Starts the portrait. If it can't run, the static print takes over. Settles once the
    portrait shows, either its first frame or the print. */
export function startPortrait(
  portrait: HTMLElement,
  hooks: PortraitHooks,
  loading: Promise<PortraitData>,
  connection: AbortSignal,
  xray: XraySource,
): Promise<void> {
  return run(portrait, hooks, loading, connection, xray).catch((error: unknown) => {
    // The element ended this run. That's not a failure, and the next connection may well
    // run live, so don't switch to the print.
    if (connection.aborted) return;
    portrait.toggleAttribute("data-static", true);
    console.warn("Portrait: showing the static print", error);
  });
}
